import React from "react";
import styled, { css } from "styled-components";
import Rule from "../../../Rule";
import Image from "../../../Image";
import Heading from "../../../Heading";
import CaseStudiesCard from "../CaseStudiesCard";
import DesignSystemsDesignTokens from "../../../../img/casestudy/design-systems/design-system-design-tokens.jpg";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";

const CaseStudyExample = styled.div`
  padding: 1.6rem;
  border-radius: var(--radius-card);
  border: var(--border-thin-inverted);
  position: relative;
  margin-top: 3.2rem;
  background: #2d2d2d;
  font-size: var(--font-size-text-small);
  &:before {
    content: "Example";
    position: absolute;
    top: -12px;
    padding: 0 8px;
    background: #242424;
    background: linear-gradient(180deg, #242424 50%, #2d2d2d 50%);
    left: 8px;
  }
`;

const TokenStructure = styled.span`
  font-size: var(--font-size-text);
  font-family: monospace;
  ${(props) =>
    props.$prefix &&
    css`
      color: #adffad;
    `}
  ${(props) =>
    props.$property &&
    css`
      color: #55ecff;
    `}
  ${(props) =>
    props.$component &&
    css`
      color: #ffc355;
    `}
`;

const TokenStructureKeyWrapper = styled.div`
  margin-bottom: var(--spacing-clear);
  @media (min-width: 510px) {
    display: grid;
    grid-template-columns: auto auto auto;
    margin-top: var(--spacing-small);
  }
`;

const TokenStructureIndividualKey = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-tiny);
  @media (min-width: 510px) {
    margin-bottom: var(--spacing-clear);
  }
`;

const TokenStructureIndividualKeyText = styled.span`
  margin-left: 0.8rem;
  font-size: var(--font-size-text-tiny);
`;

const TokenStructureKey = styled.span`
  width: 12px;
  height: 12px;
  background: blue;
  display: block;
  ${(props) =>
    props.$prefix &&
    css`
      background: #adffad;
    `}
  ${(props) =>
    props.$property &&
    css`
      background: #55ecff;
    `}
  ${(props) =>
    props.$component &&
    css`
      background: #ffc355;
    `};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-large);
  @media (min-width: 700px) {
    gap: var(--spacing-large);
    grid-template-columns: 1fr 1fr;
  }
`;

const FeelixDesignTokens = (
  <CaseStudiesCard id="designtokens">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Agnostic design tokens for a scalable, tech-independent Feelix Design System.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout wide>
      <Heading>Feelix Design Tokens</Heading>

      <Grid>
        <Box>
          <Heading variant="small">The Challenge</Heading>
          <p>
            In 2015, MYOB standardised on React as the default engineering environment. The design system team needed to
            move beyond supplying HTML and CSS snippets and find a scalable way to deliver the system’s foundations
            across products.
          </p>
          <p>
            The challenge was ensuring brand consistency not only in React but also in mobile and legacy environments.
          </p>

          <Image dark src={DesignSystemsDesignTokens} alt="Agnostic design tokens" caption="Agnostic Design Tokens" />
        </Box>
        <Stack spacing="var(--spacing-large)">
          <Box>
            <Heading variant="small">The Approach</Heading>
            <p>
              We introduced agnostic design tokens, stored design decisions such as colours, typography, and spacing
              that could be reused across platforms.
            </p>
            <ul>
              <li>Outputs for React web, React Native, and legacy stacks</li>
              <li>Tokens scale with brand updates, cascading system-wide from a source</li>
              <li>Built on Style Dictionary with clear JSON naming conventions</li>
            </ul>
            <CaseStudyExample>
              <TokenStructure $prefix>flx</TokenStructure>-<TokenStructure $property>font-size</TokenStructure>-
              <TokenStructure $component>button</TokenStructure>
              <Rule spacing="small" inverted />
              <TokenStructureKeyWrapper>
                <TokenStructureIndividualKey>
                  <TokenStructureKey $prefix />
                  <TokenStructureIndividualKeyText>Feelix prefix</TokenStructureIndividualKeyText>
                </TokenStructureIndividualKey>
                <TokenStructureIndividualKey>
                  <TokenStructureKey $property />
                  <TokenStructureIndividualKeyText>Property name</TokenStructureIndividualKeyText>
                </TokenStructureIndividualKey>
                <TokenStructureIndividualKey>
                  <TokenStructureKey $component />
                  <TokenStructureIndividualKeyText>Component name</TokenStructureIndividualKeyText>
                </TokenStructureIndividualKey>
              </TokenStructureKeyWrapper>
            </CaseStudyExample>
          </Box>
          <Box>
            <Heading variant="small">The Results</Heading>
            <p>The introduction of tokens delivered measurable impact across teams and products.</p>
            <ul>
              <li>100% coverage across Feelix components</li>
              <li>Adopted across all MYOB web, mobile, and legacy apps</li>
              <li>Faster prototyping for engineers with direct access to core values</li>
              <li>Global brand updates applied consistently through tokens</li>
              <li>Strong adoption and engagement shown in feature requests</li>
            </ul>
          </Box>
          <Box>
            <Heading variant="small">Key Insight</Heading>
            <p>
              By making foundational values universally accessible, Feelix enabled teams to build consistently across
              any stack. Design tokens provided scalability, efficiency, and alignment with design principles, while
              supporting both the future (React, React Native) and the past (legacy systems).
            </p>
          </Box>
        </Stack>
      </Grid>
    </BlogLayout>
  </CaseStudiesCard>
);

export default FeelixDesignTokens;
